import * as api from "../api";
import {
  ActionType,
  QuestionData,
  SEPARATOR,
  SurveyColumn,
  ContactType,
  Slug,
  EducationCategories,
  AccountType,
  userStr,
  VideoOrientation,
} from "../constants";
import {
  alreadyApplied,
  eduIdNotFound,
  setAllChats,
  setFullChat,
  setNumUnread,
  setProfilePicUrl,
  updateAllChats,
} from "../utils/actionUtils";
import { fixEduDescription } from "../utils/eduUtils";
import { formatJobDescription, jobIsFetti, throttle } from "../utils/utils";
import { setUserSession } from "./AuthService";
import { getReferralCode } from "./CookieService";

export const logError = (prefix, error, dispatch) => {
  let returnError = null;
  console.log("error", error);
  if (error?.response?.data?.error_code) {
    console.log(
      prefix,
      "detail error:",
      error.response.data.error_code,
      error.response.data
    );
    returnError = error.response.data;
    dispatch({ type: ActionType.error, payload: error.response.data });
  }
  if (error?.message) {
    console.log(prefix, "general error:", error.message);
    if (!returnError) {
      dispatch({
        type: ActionType.error,
        payload: { error_code: null, uncaughtError: error.message },
      });
    }
  } else {
    console.log(prefix, "full error:", error);
  }
  return returnError !== null
    ? { ...returnError, isError: true }
    : { ...error, isError: true };
};

export const register = throttle(
  async (
    dispatch,
    {
      firstName,
      lastName,
      contactInfo,
      password,
      accountType = AccountType.standard,
      contactType = ContactType.email,
    },
    handleSuccess = null
  ) => {
    try {
      const referralCode = getReferralCode();
      const ip = "none";
      const { data } = await api.register({
        firstName,
        lastName,
        contactInfo,
        password,
        contactType,
        accountType,
        referralCode,
        ip,
      });
      console.log("registerSuccess", data);
      if (data.success) {
        const {
          login_token: loginToken,
          first_name: serverFirstName,
          last_name: serverLastName,
          contact_info: serverContactInfo,
        } = data.data;
        setUserSession({
          contactInfo: serverContactInfo,
          contactType,
          loginToken,
          accountType,
          firstName: serverFirstName,
          lastName: serverLastName,
        });
        dispatch({
          type: ActionType.setUserData,
          payload: {
            firstName: serverFirstName,
            lastName: serverLastName,
            contactInfo: serverContactInfo,
            contactType,
            loginToken,
            accountType,
          },
        });
        if (handleSuccess) {
          console.log("handlingsuccess");
          handleSuccess();
        }
      } else {
        return logError("register", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("register", error, dispatch);
    }
  },
  3000
);
export const submitSurvey = throttle(
  async (dispatch, survey, handleSuccess = null) => {
    try {
      const { data } = await api.submitSurvey(survey);
      console.log("submitSurveySuccess", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess();
        }
      }
      return data;
    } catch (error) {
      return logError("submitSurvey", error, dispatch);
    }
  },
  3000
);
export const verify = throttle(
  async (dispatch, { contactInfo, loginToken, contactType, accountType }) => {
    try {
      const { data } = await api.verify({
        contactInfo,
        contactType,
        loginToken,
        accountType,
      });
      console.log("verify", data);
      if (data.success) {
        dispatch({
          type: ActionType.setUserData,
          payload: {
            contactInfo,
            contactType,
            loginToken,
            accountType,
          },
        });
      } else {
        return logError("verify", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("verify", error, dispatch);
    }
  },
  3000
);
const handleUserData = (
  dispatch,
  { contactInfo, contactType, loginToken, firstName, lastName, accountType }
) => {
  setUserSession({
    contactInfo,
    contactType,
    loginToken,
    firstName,
    lastName,
    accountType,
  });
  dispatch({
    type: ActionType.setUserData,
    payload: {
      contactInfo,
      contactType,
      loginToken,
      firstName,
      lastName,
      accountType,
    },
  });
};
export const login = throttle(
  async (
    dispatch,
    navigate,
    {
      contactInfo,
      password,
      contactType,
      stayLoggedIn,
      // jobToken is for nav->jobResults
      // eduId is for nav->university
      jobToken,
      eduId,
    }
  ) => {
    try {
      const { data } = await api.login({
        contactInfo,
        password,
        contactType,
        stayLoggedIn,
      });
      console.log("login", data);
      if (data.success) {
        const {
          login_token: loginToken,
          first_name: firstName,
          last_name: lastName,
          contact_info: serverContactInfo,
        } = data.data;
        handleUserData(dispatch, {
          contactInfo: serverContactInfo,
          contactType,
          loginToken,
          firstName,
          lastName,
        });
        getSurvey(dispatch, {
          contactInfo: serverContactInfo,
          contactType,
          loginToken,
        });
        navigate(
          jobToken
            ? `${Slug.jobResults}/?job_id=${jobToken}`
            : eduId
            ? `${Slug.education}/?tag=all&post=${eduId}`
            : Slug.jobResults
        );
      } else {
        return logError("login", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("login", error, dispatch);
    }
  },
  3000
);

export const getSurveyNoThrottle = async (
  dispatch,
  { contactInfo, contactType, loginToken, firstName, lastName },
  handleFail = null
) => {
  try {
    const { data } = await api.surveyData({
      contactInfo,
      contactType,
      loginToken,
    });
    console.log("getSurvey", data);
    if (data.success) {
      const { survey_data, user_info } = data.data;
      if (survey_data) {
        dispatch({
          type: ActionType.setUserData,
          payload: {
            contactInfo,
            contactType,
            loginToken,
            firstName: user_info.first_name,
            lastName: user_info.last_name,
            verified: user_info.verified,
            isAdmin: user_info.is_admin,
            accountType: user_info.account_type,
          },
        });
        setUserSession({
          contactInfo,
          contactType,
          loginToken,
          firstName: user_info.first_name,
          lastName: user_info.last_name,
        });
        console.log("got survey data", survey_data);
        const surveyPayload = {};
        QuestionData.forEach(
          ({ column, options, useInputText, isImportant }, i) => {
            // console.log(column, i)
            if (
              column === null ||
              survey_data[column] === undefined ||
              survey_data[column] === null
            )
              return;
            let columnData = survey_data[column].split(SEPARATOR);
            if (column === SurveyColumn.salary) {
              // convert to number
              columnData = columnData.map((s) => parseInt(s));
              dispatch({
                type: ActionType.updateDefaultSalaryIdx,
                payload: options.indexOf(columnData[0]) + 1,
              });
              surveyPayload[i] = columnData
                .map((s, j) => options.indexOf(s) + 1)
                .filter((s) => s !== -1);
            }
            if (useInputText) {
              surveyPayload[i] = columnData;
              return;
            }
            if (survey_data[column] !== "") {
              columnData.forEach((s, j) => {
                if (options.indexOf(s) === -1) {
                  console.log(
                    "unknown survey data",
                    column,
                    s,
                    options.indexOf(s)
                  );
                }
              });
            }
            surveyPayload[i] = columnData
              .map((s, j) => options.indexOf(s))
              .filter((s) => s !== -1);
            if (
              isImportant &&
              survey_data[column + "_important"] !== undefined &&
              survey_data[column + "_important"] !== null
            ) {
              surveyPayload["important" + i] = survey_data[
                column + "_important"
              ]
                .split(SEPARATOR)
                .map((s, j) => options.indexOf(s))
                .filter((s) => s !== -1);
            }
          }
        );
        // console.log('got survey data2', surveyPayload)
        dispatch({
          type: ActionType.updateSurveyFromServer,
          payload: surveyPayload,
        });
      }
    } else {
      return logError("getSurvey", data.error_code, dispatch);
    }
    return data;
  } catch (error) {
    if (handleFail) {
      handleFail();
    }
    return logError("getSurvey", error, dispatch);
  }
};
export const getSurvey = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, firstName, lastName },
    handleFail = null,
    handleSuccess = null
  ) => {
    try {
      const { data } = await api.surveyData({
        contactInfo,
        contactType,
        loginToken,
      });
      console.log("getSurvey", data);
      if (data.success) {
        const { survey_data, user_info } = data.data;
        if (survey_data) {
          dispatch({
            type: ActionType.setUserData,
            payload: {
              contactInfo,
              contactType,
              loginToken,
              firstName: user_info.first_name,
              lastName: user_info.last_name,
              verified: user_info.verified,
              isAdmin: user_info.is_admin,
              accountType: user_info.account_type,
            },
          });
          setUserSession({
            contactInfo,
            contactType,
            loginToken,
            firstName: user_info.first_name,
            lastName: user_info.last_name,
          });
          console.log("got survey data", survey_data);
          const surveyPayload = {};
          QuestionData.forEach(
            ({ column, options, useInputText, isImportant }, i) => {
              // console.log(column, i)
              if (
                column === null ||
                survey_data[column] === undefined ||
                survey_data[column] === null
              )
                return;
              let columnData = survey_data[column].split(SEPARATOR);
              if (column === SurveyColumn.salary) {
                // convert to number
                columnData = columnData.map((s) => parseInt(s));
                dispatch({
                  type: ActionType.updateDefaultSalaryIdx,
                  payload: options.indexOf(columnData[0]) + 1,
                });
                surveyPayload[i] = columnData
                  .map((s, j) => options.indexOf(s) + 1)
                  .filter((s) => s !== -1);
              }
              if (useInputText) {
                surveyPayload[i] = columnData;
                return;
              }
              if (survey_data[column] !== "") {
                columnData.forEach((s, j) => {
                  if (options.indexOf(s) === -1) {
                    console.log(
                      "unknown survey data",
                      column,
                      s,
                      options.indexOf(s)
                    );
                  }
                });
              }
              surveyPayload[i] = columnData
                .map((s, j) => options.indexOf(s))
                .filter((s) => s !== -1);
              if (
                isImportant &&
                survey_data[column + "_important"] !== undefined &&
                survey_data[column + "_important"] !== null
              ) {
                surveyPayload["important" + i] = survey_data[
                  column + "_important"
                ]
                  .split(SEPARATOR)
                  .map((s, j) => options.indexOf(s))
                  .filter((s) => s !== -1);
              }
            }
          );
          // console.log('got survey data2', surveyPayload)
          dispatch({
            type: ActionType.updateSurveyFromServer,
            payload: surveyPayload,
          });
          if (handleSuccess) {
            handleSuccess();
          }
        }
      } else {
        return logError("getSurvey", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      if (handleFail) {
        handleFail();
      }
      return logError("getSurvey", error, dispatch);
    }
  },
  3000
);

const rawGetEdu = async (dispatch, { tag, skipCount }) => {
  try {
    const { data } = await api.getEdu({ tag, skipCount });
    console.log("getEdu", data);
    const eduPayload = [];
    data.data.result.forEach((job, i) => {
      const [
        eduId,
        title,
        subtitle,
        author,
        description_json,
        show_idx,
        last_element_json,
        download_name,
        tags,
      ] = job;
      try {
        JSON.parse(description_json);
        if (last_element_json !== null) {
          JSON.parse(last_element_json);
        }
        const newEdu = {
          eduId,
          title,
          subtitle,
          author,
          // description: JSON.parse(description_json),
          description: fixEduDescription(JSON.parse(description_json)),
          lastElement:
            last_element_json === null ? null : JSON.parse(last_element_json),
          showIdx: parseInt(show_idx),
          download_name,
          tags: tags.split(","),
        };
        eduPayload.push(newEdu);
      } catch (e) {
        console.log("error parsing", description_json, e);
      }
    });
    dispatch({
      type: ActionType.addEdu,
      payload: { panels: eduPayload, tag, totalCount: data.data.total_count },
    });
    return data;
  } catch (error) {
    return logError("getEdu", error, dispatch);
  }
};
const eduThrottleFuncs = {};
EducationCategories.forEach((tag) => {
  eduThrottleFuncs[tag] = throttle(rawGetEdu, 2000);
});

export const getEdu = (dispatch, { tag, skipCount }) => {
  return eduThrottleFuncs[tag](dispatch, { tag, skipCount });
};

export const getEduById = throttle(
  async (dispatch, { eduId }, handleNotFound = null) => {
    try {
      const { data } = await api.getEduById({ eduId });
      console.log("getEduById", data);
      const { result } = data.data;
      if (!result) {
        console.log("no edu found", eduId);
        eduIdNotFound(dispatch, eduId);
        if (handleNotFound) {
          handleNotFound();
        }
      } else {
        const eduPayload = [];
        const [
          eduId,
          title,
          subtitle,
          author,
          description_json,
          show_idx,
          last_element_json,
          download_name,
          tags,
        ] = result;
        try {
          JSON.parse(description_json);
          if (last_element_json !== null) {
            JSON.parse(last_element_json);
          }
          const newEdu = {
            eduId,
            title,
            subtitle,
            author,
            // description: JSON.parse(description_json),
            description: fixEduDescription(JSON.parse(description_json)),
            lastElement:
              last_element_json === null ? null : JSON.parse(last_element_json),
            showIdx: parseInt(show_idx),
            download_name,
            tags: tags.split(","),
          };
          eduPayload.push(newEdu);
        } catch (e) {
          console.log("error parsing", description_json, e);
        }
        dispatch({
          type: ActionType.addEdu,
          payload: { panels: eduPayload, tag: null, totalCount: 1 },
        });
        return data;
      }
    } catch (error) {
      return logError("getEdu", error, dispatch);
    }
  },
  2000
);

// export const getEdu = throttle(async (dispatch, { tag, skipCount }) => {
//   try {
//     const { data } = await api.getEdu({ tag, skipCount });
//     console.log("getEdu", data);
//     const eduPayload = [];
//     data.data.result.forEach((job, i) => {
//       const [
//         eduId,
//         title,
//         subtitle,
//         author,
//         description_json,
//         show_idx,
//         last_element_json,
//         download_name,
//         tags,
//       ] = job;
//       try {
//         JSON.parse(description_json);
//         if (last_element_json !== null) {
//           JSON.parse(last_element_json);
//         }
//         const newEdu = {
//           eduId,
//           title,
//           subtitle,
//           author,
//           // description: JSON.parse(description_json),
//           description: fixEduDescription(JSON.parse(description_json)),
//           lastElement:
//             last_element_json === null ? null : JSON.parse(last_element_json),
//           showIdx: parseInt(show_idx),
//           download_name,
//           tags: tags.split(","),
//         };
//         eduPayload.push(newEdu);
//       } catch (e) {
//         console.log("error parsing", description_json, e);
//       }
//     });
//     dispatch({
//       type: ActionType.addEdu,
//       payload: { panels: eduPayload, tag, totalCount: data.data.total_count },
//     });
//     return data;
//   } catch (error) {
//     return logError("getEdu", error, dispatch);
//   }
// }, 1000);

// conversion between lambda's _sql_to_job_data to client job data
const parseFettiJob = (job) => {
  const [
    native_id,
    job_id,
    job_title,
    company,
    job_description,
    bullets_json, // actually is the skills section
    tag_salary,
    tag_job_type,
    url,
    location_token,
    role_token,
    experience_token,
    is_fav,
    vid_url,
    vid_size,
  ] = job;

  let location;
  let locations;
  if (location_token === "All") {
    location = "All Locations";
    locations = ["All Locations"];
  } else {
    locations = location_token.split(SEPARATOR).map((s) => s.split(",")[0]);
    location = locations[0];
  }
  const newJob = {
    jobId: native_id,
    isFav: is_fav,
    company,
    jobTitle: job_title,
    location,
    locations,
    roleToken: role_token,
    jobDescription: job_description,
    bullets: null,
    skills: bullets_json.split(SEPARATOR),
    experienceLevel: experience_token,
    url,
    salaryData: tag_salary,
    vidUrl: vid_url ? vid_url : null,
    vidSize: vid_size ? vid_size : VideoOrientation.landscape,
  };
  return newJob;
};

export const getJobs = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken },
    jobToken = null,
    appToken = null
  ) => {
    try {
      const { data } = await api.getJobs({
        contactInfo,
        contactType,
        loginToken,
        jobToken,
        appToken,
      });
      console.log("getJobs", data);
      const jobPayload = [];
      const fettiJobs = [];
      let firstJob = null;
      let firstAppJob = null;
      data.data.result.forEach((job, i) => {
        const [
          native_id,
          job_id,
          job_title,
          company,
          job_description,
          bullets_json,
          tag_salary,
          tag_job_type,
          url,
          location_token,
          role_token,
          experience_token,
          is_fav,
          // for extra "fetti" data, refer to parseFettiJob
          // vid_url,
        ] = job;
        if (jobIsFetti(native_id)) {
          try {
            // parsing for fetti jobs
            const newJob = parseFettiJob(job);
            if (data.data.put_app_id === native_id) {
              console.log(
                "found app job id: ",
                native_id,
                " at index ",
                i,
                " of ",
                data.data.result.length,
                " jobs"
              );
              firstAppJob = newJob;
            } else {
              // jobPayload.push(newJob);
              fettiJobs.push(newJob);
            }
          } catch (e) {
            console.log("error parsing", job_description, e);
          }
        } else {
          //parsing for normal jobs
          try {
            JSON.parse(job_description);
            JSON.parse(bullets_json);
            const newJob = {
              jobId: native_id,
              isFav: is_fav,
              company,
              jobTitle: job_title,
              location:
                location_token === "All" ? "All Locations" : location_token,
              roleToken: role_token,
              jobDescription: formatJobDescription(job_description),
              bullets: JSON.parse(bullets_json),
              experienceLevel: experience_token,
              vidUrl: null,
              url,
            };
            if (data.data.put_first_id === native_id) {
              console.log(
                "found first job id: ",
                native_id,
                " at index ",
                i,
                " of ",
                data.data.result.length,
                " jobs"
              );
              firstJob = newJob;
            } else {
              jobPayload.push(newJob);
            }
          } catch (e) {
            console.log("error parsing", job_description, e);
          }
        }
      });

      // shuffle jobs
      // pseudo-random, should change once a day
      let seed = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
      const dailyRandom = () => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
      };
      if (fettiJobs.length > 0) {
        jobPayload.unshift(...fettiJobs);
      }
      for (let i = jobPayload.length - 1; i > 0; i--) {
        // const j = Math.floor(Math.random() * (i + 1));
        const j = Math.floor(dailyRandom() * (i + 1));
        [jobPayload[i], jobPayload[j]] = [jobPayload[j], jobPayload[i]];
      }
      if (firstJob) {
        jobPayload.unshift(firstJob);
      }
      if (firstAppJob) {
        jobPayload.unshift(firstAppJob);
      }

      dispatch({ type: ActionType.setJobs, payload: jobPayload });
      return data;
    } catch (error) {
      return logError("getJobs", error, dispatch);
    }
  },
  3000
);
export const toggleFavJob = throttle(
  async (dispatch, { contactInfo, contactType, loginToken, jobId, isFav }) => {
    dispatch({ type: ActionType.toggleFavJob, payload: { jobId, isFav } });
    try {
      const { data } = await api.toggleFavJob({
        contactInfo,
        contactType,
        loginToken,
        jobId,
        isFav,
      });
      console.log("toggleFavJobSuccess", data);
      if (data.success) {
      } else {
        return logError("toggleFavJob", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("toggleFavJob", error, dispatch);
    }
  },
  100
);
export const updateSettings = throttle(
  async (dispatch, { contactInfo, contactType, loginToken, settings }) => {
    try {
      const { data } = await api.updateSettings({
        contactInfo,
        contactType,
        loginToken,
        settings,
      });
      console.log("updateSettingsSuccess", data);
      if (data.success) {
        getSurveyNoThrottle(dispatch, { contactInfo, contactType, loginToken });
      } else {
        return logError("updateSettings", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("updateSettings", error, dispatch);
    }
  },
  1000
);
export const updatePassword = throttle(
  async (
    dispatch,
    {
      contactInfo,
      contactType,
      currentPassword,
      newPassword,
      confirmNewPassword,
    },
    handleSuccess
  ) => {
    try {
      const { data } = await api.updatePassword({
        contactInfo,
        contactType,
        currentPassword,
        newPassword,
        confirmNewPassword,
      });
      console.log("updatePasswordSuccess", data);
      if (data.success) {
        const {
          login_token: loginToken,
          first_name: firstName,
          last_name: lastName,
        } = data.data;
        handleUserData(dispatch, {
          contactInfo,
          contactType,
          loginToken,
          firstName,
          lastName,
        });
        handleSuccess();
      } else {
        return logError("updatePassword", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("updatePassword", error, dispatch);
    }
  },
  3000
);
export const deleteAccount = throttle(
  async (dispatch, { contactInfo, contactType, loginToken }, handleSuccess) => {
    try {
      const { data } = await api.deleteAccount({
        contactInfo,
        contactType,
        loginToken,
      });
      console.log("deleteAccountSuccess", data);
      if (data.success) {
        handleSuccess();
      } else {
        return logError("deleteAccount", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("deleteAccount", error, dispatch);
    }
  },
  3000
);
export const requestResetPassword = throttle(
  async (dispatch, { contactInfo }, handleSuccess) => {
    try {
      const { data } = await api.requestResetPassword({
        contactInfo,
      });
      console.log("requestResetPasswordSuccess", data);
      if (data.success) {
        handleSuccess();
      } else {
        return logError("requestResetPassword", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("requestResetPassword", error, dispatch);
    }
  },
  15000
);
export const resetPassword = throttle(
  async (dispatch, { contactInfo, token, newPassword }, handleSuccess) => {
    try {
      const { data } = await api.resetPassword({
        contactInfo,
        token,
        newPassword,
      });
      console.log("resetPasswordSuccess", data);
      if (data.success) {
        handleSuccess();
      } else {
        return logError("resetPassword", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("resetPassword", error, dispatch);
    }
  },
  3000
);
export const requestVerifyEmail = throttle(
  async (dispatch, { contactInfo, contactType }) => {
    try {
      const { data } = await api.requestVerifyEmail({
        contactInfo,
        contactType,
      });
      console.log("requestVerifyEmailSuccess", data);
      if (data.success) {
        // handleSuccess();
      } else {
        return logError("requestVerifyEmail", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("requestVerifyEmail", error, dispatch);
    }
  },
  15000
);
export const verifyEmail = throttle(
  async (dispatch, { contactInfo, token }, handleSuccess) => {
    try {
      const { data } = await api.verifyEmail({
        contactInfo,
        token,
      });
      console.log("verifyEmailSuccess", data);
      if (data.success) {
        handleSuccess();
      } else {
        return logError("verifyEmail", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("verifyEmail", error, dispatch);
    }
  },
  3000
);
export const unsubscribe = throttle(
  async (dispatch, { contactInfo, token }) => {
    try {
      const { data } = await api.unsubscribe({
        contactInfo,
        token,
      });
      console.log("unsubscribeSuccess", data);
      if (data.success) {
        console.log("unsubscribeSuccess in data");
      } else {
        return logError("unsubscribe", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("unsubscribe", error, dispatch);
    }
  },
  3000
);
export const getApplyData = throttle(
  async (
    dispatch,
    navigate,
    { contactInfo, contactType, loginToken, jobId }
  ) => {
    try {
      const { data } = await api.getApplyData({
        contactInfo,
        contactType,
        loginToken,
        jobId,
      });
      console.log("getApplyData", data);
      if (data.success) {
        const { applied, about_company, job, logo_url, resume_names, user } =
          data.data;
        if (applied) {
          alreadyApplied(navigate);
          return;
        }
        const [firstName, lastName, email, location, phone] = user;
        const resume = resume_names.length > 0 ? resume_names[0] : null;
        const [
          native_id,
          job_id,
          job_title,
          company,
          job_description,
          bullets_json,
          tag_salary,
          tag_job_type,
          url,
          location_token,
          role_token,
          experience_token,
          is_fav,
          vid_url,
          vid_size,
          questions_json,
        ] = job;
        const questionsData = JSON.parse(questions_json);
        const questions = [];
        questionsData.forEach((q) => {
          if (q.isCustom || !q.fieldName) {
            questions.push({
              question: q.question,
              type: q.type,
              answerRange: q.expectedAnswerRange,
            });
          } else {
            questions.push({
              question: q.question.replace(q.fieldName, q.fieldValue),
              type: q.type,
              answerRange: q.expectedAnswerRange,
            });
          }
        });
        console.log("questions", questions);
        const app = {
          firstName,
          lastName,
          email,
          location: location === null ? "" : location,
          phone: phone === null ? "" : phone,
          resume: resume === null ? "" : resume,
          questions: questions.map((q) => ""),
        };
        const jobData = parseFettiJob(job);
        const serverData = {
          questions,
          logoUrl: logo_url,
          aboutCompany: about_company,
          resumes: resume_names,
        };
        dispatch({
          type: ActionType.setApplyData,
          payload: { app, jobData, serverData, jobId: native_id },
        });
      } else {
        return logError("getApplyData", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("getApplyData", error, dispatch);
    }
  },
  1000
);
export const jobClick = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, jobId, clickType }
  ) => {
    try {
      const { data } = await api.jobClick({
        contactInfo,
        contactType,
        loginToken,
        jobId,
        clickType,
      });
      console.log("jobClickSuccess", data);
      if (data.success) {
        // console.log("jobClickSuccess in data");
      } else {
        return logError("jobClick", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("jobClick", error, dispatch);
    }
  },
  10
);
export const eduClick = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, eduId, clickType }
  ) => {
    try {
      const { data } = await api.eduClick({
        contactInfo,
        contactType,
        loginToken,
        eduId,
        clickType,
      });
      console.log("eduClickSuccess", data);
      if (data.success) {
      } else {
        return logError("eduClick", data.error_code, dispatch);
      }
      return data;
    } catch (error) {
      return logError("eduClick", error, dispatch);
    }
  },
  10
);

export const logAffiliateLanded = throttle(
  async (
    dispatch,
    { token, isNewRef, ip = "none" },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.logAffiliateLanded({
        token,
        ip,
      });
      console.log("logAffiliateLandedSuccess", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess(data.data);
        }
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("logAffiliateLanded", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("logAffiliateLanded", error, dispatch);
    }
  },
  10000
);
export const uploadResume = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, fileName, filePath, fileData },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.uploadResume({
        contactInfo,
        contactType,
        loginToken,
        fileName,
        filePath,
        fileData,
      });
      // handling here is from binary request
      const body = JSON.parse(data.body);
      console.log("uploadResume", body);
      if (body.success) {
        getResumeUploads(
          dispatch,
          { contactInfo, contactType, loginToken },
          { handleSuccess }
        );
        // todo: reload server resumes
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("uploadResume", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("uploadResume", error, dispatch);
    }
  },
  1500
);

export const getResumeUploads = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getResumeUploads({
        contactInfo,
        contactType,
        loginToken,
      });
      console.log("getResumeUploads", data);
      if (data.success) {
        const { resume_names } = data.data;
        // resume_names is a misnomer. resume_names = [[id, resume_name], ...]]
        const resume = resume_names.length > 0 ? resume_names[0] : null;
        const app = {
          resume: resume === null ? "" : resume,
        };
        const serverData = {
          resumes: resume_names,
        };
        dispatch({
          type: ActionType.updateApplyServerData,
          payload: serverData,
        });
        dispatch({
          type: ActionType.updateAppData,
          payload: app,
        });
        if (handleSuccess) {
          handleSuccess();
        }
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("getResumeUploads", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("getResumeUploads", error, dispatch);
    }
  },
  1000
);
export const getResumeUploads2 = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getResumeUploads({
        contactInfo,
        contactType,
        loginToken,
      });
      console.log("getResumeUploads", data);
      if (data.success) {
        const { resume_names } = data.data;
        // resume_names is a misnomer. resume_names = [[id, resume_name], ...]]
        const resume = resume_names.length > 0 ? resume_names[0] : null;
        const app = {
          resume: resume === null ? "" : resume,
        };
        const serverData = {
          resumes: resume_names,
        };
        dispatch({
          type: ActionType.updateApplyServerData,
          payload: serverData,
        });
        dispatch({
          type: ActionType.updateAppData,
          payload: app,
        });
        if (handleSuccess) {
          handleSuccess();
        }
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("getResumeUploads", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("getResumeUploads", error, dispatch);
    }
  },
  1000
);
export const getResumeUrl = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, resumeId },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getResume({
        contactInfo,
        contactType,
        loginToken,
        resumeId,
      });
      console.log("getResumeUrl", data);
      if (data.success) {
        const { url } = data.data;
        dispatch({
          type: ActionType.setResumeUrl,
          payload: {
            resumeId,
            url,
          },
        });
        if (handleSuccess) {
          handleSuccess();
        }
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("getResumeUrl", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("getResumeUrl", error, dispatch);
    }
  },
  100
);

export const submitApplication = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, application, jobId },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.submitApplication({
        contactInfo,
        contactType,
        loginToken,
        application,
        jobId,
      });
      console.log("submitApplication", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess();
        }
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("submitApplication", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("submitApplication", error, dispatch);
    }
  },
  1000
);

export const getChats = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, latestId = 0 },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getChats({
        contactInfo,
        contactType,
        loginToken,
        latestId,
      });
      console.log("getChats", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess(data.data);
        }
        const { chats, user_id: userId, num_unread: numUnread } = data.data;
        const allChats = [];
        chats.forEach((chatData) => {
          const [
            msg_id,
            dt_created,
            message,
            application_id,
            sender_is_applicant,
            seen,
            first_name,
            last_name,
            email,
            recruiter_id,
          ] = chatData;
          const chatUser = {
            userId: recruiter_id,
            firstName: first_name,
            lastName: last_name,
            email: email,
          };
          const chatMessage = {
            serverId: msg_id,
            userId: sender_is_applicant ? userStr : recruiter_id,
            dt: dt_created,
            text: message,
            applicationId: application_id,
            senderIsApplicant: sender_is_applicant,
            seen: seen,
          };
          const chat = {
            recipient: chatUser,
            history: [chatMessage],
          };
          allChats.push(chat);
        });
        if (latestId === null || latestId === 0) {
          console.log("setting chats", allChats);
          setAllChats(dispatch, allChats);
        } else {
          console.log("updating chats", allChats);
          updateAllChats(dispatch, allChats);
        }
        // set num unread
        setNumUnread(dispatch, numUnread);
      } else {
        if (handleFinish) {
          handleFinish();
        }
        if (handleError) {
          handleError(data.error_code);
        }
        return logError("getChats", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleFinish) {
        handleFinish();
      }
      if (handleError) {
        handleError(error);
      }
      return logError("getChats", error, dispatch);
    }
  },
  3000
);

export const getFullChat = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, recruiterId },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getFullChat({
        contactInfo,
        contactType,
        loginToken,
        recruiterId,
      });
      console.log("getFullChat", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess(data.data);
        }
        const {
          chats,
          user_id: userId,
          recipient,
          num_unread: numUnread,
        } = data.data;
        const [first_name, last_name, email, recruiter_id] = recipient;
        const chatUser = {
          userId: recruiter_id,
          firstName: first_name,
          lastName: last_name,
          email,
        };
        const history = [];
        chats.forEach((chatData) => {
          const [
            msg_id,
            dt_created,
            message,
            application_id,
            sender_is_applicant,
            seen,
            // first_name,
            // last_name,
            // email,
            // recruiter_id,
          ] = chatData;
          // chatUser = {
          //   userId: recruiter_id,
          //   firstName: first_name,
          //   lastName: last_name,
          //   email: email,
          // }; // yes, this is set repeatedly
          const chatMessage = {
            serverId: msg_id,
            userId: sender_is_applicant ? userStr : recruiter_id,
            dt: dt_created,
            text: message,
            applicationId: application_id,
            senderIsApplicant: sender_is_applicant,
            seen: seen,
          };
          history.push(chatMessage);
        });
        // sort by dt, recent LAST
        history.sort((a, b) => {
          const aDt = a.dt;
          const bDt = b.dt;
          if (aDt < bDt) {
            return -1;
          }
          if (aDt > bDt) {
            return 1;
          }
          return 0;
        });
        const chat = {
          recipient: chatUser,
          history: history,
          fullyLoaded: true,
        };
        console.log("setting full chat", chat);
        setFullChat(dispatch, chat);
        // set num unread
        setNumUnread(dispatch, numUnread);
      } else {
        if (handleFinish) {
          handleFinish();
        }
        if (handleError) {
          handleError(data.error_code);
        }
        return logError("getFullChat", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleFinish) {
        handleFinish();
      }
      if (handleError) {
        handleError(error);
      }
      return logError("getFullChat", error, dispatch);
    }
  },
  3000
);
export const getApplicationData = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, applicationId },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.getApplicationData({
        contactInfo,
        contactType,
        loginToken,
        applicationId,
      });
      console.log("getApplicationData", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess(data.data);
        }
        const [
          dt,
          title,
          primary_role,
          locations,
          company_name,
          logo_url,
          token,
        ] = data.data.application;
        dispatch({
          type: ActionType.setApplicationAttachmentData,
          payload: {
            [applicationId]: {
              dt,
              title,
              primaryRole: primary_role,
              locations,
              companyName: company_name,
              logoUrl: logo_url,
              token,
            },
          },
        });
      } else {
        if (handleFinish) {
          handleFinish();
        }
        if (handleError) {
          handleError(data.error_code);
        }
        return logError("getApplicationData", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleFinish) {
        handleFinish();
      }
      if (handleError) {
        handleError(error);
      }
      return logError("getApplicationData", error, dispatch);
    }
  },
  3000
);
export const messageBasic = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, recruiterId, message },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.messageBasic({
        contactInfo,
        contactType,
        loginToken,
        recruiterId,
        message,
      });
      console.log("messageBasic", data);
      if (data.success) {
        if (handleSuccess) {
          handleSuccess(data);
        }
      } else {
        if (handleFinish) {
          handleFinish();
        }
        if (handleError) {
          handleError(data.error_code);
        }
        return logError("messageBasic", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleFinish) {
        handleFinish();
      }
      if (handleError) {
        handleError(error);
      }
      return logError("messageBasic", error, dispatch);
    }
  },
  300
);

const createApiCall = (
  funcName,
  apiFunc,
  throttleTime = 1000,
  { defaultSuccess = null, defaultFinish = null, defaultError = null }
) => {
  return throttle(
    async (
      dispatch,
      navigate,
      params,
      { handleSuccess = null, handleFinish = null, handleError = null }
    ) => {
      try {
        console.log("api call", funcName, params);
        const { data } = await apiFunc(params);
        console.log(funcName, data);
        if (data.success) {
          if (handleSuccess) {
            handleSuccess(data);
          }
          if (defaultSuccess) {
            defaultSuccess(dispatch, navigate, params, data);
          }
        } else {
          if (handleFinish) {
            handleFinish();
          }
          if (handleError) {
            handleError(data.error_code);
          }
          if (defaultFinish) {
            defaultFinish(dispatch, navigate, params);
          }
          if (defaultError) {
            defaultError(dispatch, navigate, params, data.error_code);
          }
          return logError(funcName, data.error_code, dispatch);
        }
        if (handleFinish) {
          handleFinish();
        }
        if (defaultFinish) {
          defaultFinish(dispatch, navigate, params);
        }
        return data;
      } catch (error) {
        if (handleFinish) {
          handleFinish();
        }
        if (handleError) {
          handleError(error);
        }
        if (defaultFinish) {
          defaultFinish(dispatch, navigate, params);
        }
        if (defaultError) {
          defaultError(dispatch, navigate, params, error);
        }
        return logError(funcName, error, dispatch);
      }
    },
    throttleTime
  );
};
export const getSchools = createApiCall("getSchools", api.getSchools, 300, {
  defaultSuccess: (dispatch, navigate, params, data) => {
    const { schools } = data.data;
    const schoolIds = [];
    const schoolData = {};
    schools.forEach((school) => {
      const [
        school_id,
        school_name,
        degree,
        major,
        start_month,
        start_year,
        end_month,
        end_year,
        grade,
        activities,
      ] = school;
      const schoolId = parseInt(school_id);
      schoolIds.push(schoolId);
      schoolData[schoolId] = {
        school_name,
        degree,
        major,
        start_month,
        start_year,
        end_month,
        end_year,
        grade,
        activities,
      };
    });
    dispatch({
      type: ActionType.updateSchools,
      payload: {
        schoolIds,
        schoolData,
      },
    });
  },
});
export const addSchool = createApiCall("addSchool", api.addSchool, 300, {
  defaultSuccess: (dispatch, navigate, params, data) => {
    const { school_id } = data.data;
    const schoolId = parseInt(school_id);
    dispatch({
      type: ActionType.updateSchools,
      payload: {
        schoolIds: [schoolId],
        schoolData: {
          [schoolId]: params.schoolData,
        },
      },
    });
  },
});
export const updateSchool = createApiCall(
  "updateSchool",
  api.updateSchool,
  300,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      const { schoolId, schoolData } = params;
      dispatch({
        type: ActionType.updateSchools,
        payload: {
          schoolIds: [schoolId],
          schoolData: {
            [schoolId]: schoolData,
          },
        },
      });
    },
  }
);
export const deleteSchool = createApiCall(
  "deleteSchool",
  api.deleteSchool,
  300,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      dispatch({
        type: ActionType.deleteSchool,
        payload: params.schoolId,
      });
    },
  }
);

export const getOwnProfilePic = createApiCall(
  "getOwnProfilePic",
  api.getOwnProfilePic,
  300,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      const { url, scale } = data.data;
      console.log("got profile pic", url, scale);
      setProfilePicUrl(dispatch, url, scale);
    },
  }
);
export const deleteOwnProfilePic = createApiCall(
  "deleteOwnProfilePic",
  api.deleteOwnProfilePic,
  200,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      setProfilePicUrl(dispatch, null, null);
    },
  }
);

// todo: if handleSuccess is needed, we need to
// determine if it should be passed into the following setProfilePicUrl
export const uploadProfilePic = throttle(
  async (
    dispatch,
    { contactInfo, contactType, loginToken, fileData, fileName, scale },
    { handleSuccess = null, handleFinish = null, handleError = null }
  ) => {
    try {
      const { data } = await api.uploadProfilePic({
        contactInfo,
        contactType,
        loginToken,
        fileData,
        fileName,
        scale,
      });
      // handling here is from binary request
      const body = JSON.parse(data.body);
      console.log("uploadProfilePic", body);
      if (body.success) {
        console.log("success! refetching profile pic", body);
        const { url } = body.data;
        setProfilePicUrl(dispatch, url, scale);
        // todo: reload server resumes
      } else {
        if (handleError) {
          handleError();
        }
        if (handleFinish) {
          handleFinish();
        }
        return logError("uploadProfilePic", data.error_code, dispatch);
      }
      if (handleFinish) {
        handleFinish();
      }
      return data;
    } catch (error) {
      if (handleError) {
        handleError();
      }
      if (handleFinish) {
        handleFinish();
      }
      return logError("uploadProfilePic", error, dispatch);
    }
  },
  1500
);

export const getCompanyInfoByJobId = createApiCall(
  "getCompanyInfo",
  api.getCompanyInfoByJobId,
  200,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      const {
        name: companyName,
        url: companyUrl,
        logo_url: logoUrl,
        work_values: workValues,
        vid_url: vidUrl,
        locations,
        sectors,
        size,
        pitch,
        about_company: aboutCompany,
        about_product: aboutProduct,
      } = data.data.data; // i didn't write this monstrosity today, copied from EDA
      const payload = {
        [params.jobId]: {
          companyName,
          companyUrl,
          logoUrl,
          vidUrl,
          locations,
          sectors,
          workValues,
          size,
          pitch,
          aboutCompany,
          aboutProduct,
        },
      };
      dispatch({
        type: ActionType.updateServerCompany,
        payload,
      });
    },
  }
);

const recruiterProfilePicRequests = {};
export const applicantgetRecruiterProfilePic = async (
  dispatch,
  navigate,
  { contactInfo, contactType, loginToken, recruiterId },
  { handleSuccess = null, handleFinish = null, handleError = null }
) => {
  if (recruiterProfilePicRequests[recruiterId]) {
    if (Date.now() - recruiterProfilePicRequests[recruiterId] < 1000) {
      console.log("bouncing request for pic", recruiterId);
      return;
    }
  }
  recruiterProfilePicRequests[recruiterId] = Date.now();
  try {
    const { data } = await api.applicantgetRecruiterProfilePic({
      contactInfo,
      contactType,
      loginToken,
      recruiterId,
    });
    console.log("applicantgetRecruiterProfilePic", data);
    if (data.success) {
      if (handleSuccess) {
        handleSuccess(data.data);
      }
      const { url, scale } = data.data;
      if (url) {
        dispatch({
          type: ActionType.setRecipientProfilePic,
          payload: {
            userId: recruiterId,
            profilePic: { url, scale },
          },
        });
      } else {
        dispatch({
          type: ActionType.setRecipientProfilePic,
          payload: {
            userId: recruiterId,
            profilePic: null,
          },
        });
      }
    } else {
      if (handleFinish) {
        handleFinish();
      }
      if (handleError) {
        handleError(data.error_code);
      }
      return logError(
        "applicantgetRecruiterProfilePic",
        data.error_code,
        dispatch
      );
    }
    return data;
  } catch (error) {
    if (handleFinish) {
      handleFinish();
    }
    if (handleError) {
      handleError(error);
    }
    return logError("applicantgetRecruiterProfilePic", error, dispatch);
  }
};
export const getNumUnread = createApiCall(
  "getNumUnread",
  api.getNumUnread,
  200,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      const { num_unread: numUnread } = data.data;
      setNumUnread(dispatch, numUnread);
    },
  }
);
export const getAllApplicationData = createApiCall(
  "getAllApplicationData",
  api.getAllApplicationData,
  200,
  {
    defaultSuccess: (dispatch, navigate, params, data) => {
      const { applications } = data.data;
      const appIds = [];
      const appData = {};
      applications.forEach((app) => {
        const [
          dt,
          appId,
          title,
          primaryRole,
          locations,
          companyName,
          logoUrl,
          token,
        ] = app;
        const applicationId = parseInt(appId);
        appIds.push(applicationId);
        appData[applicationId] = {
          dt,
          title,
          primaryRole,
          locations,
          companyName,
          logoUrl,
          token,
        };
      });
      dispatch({
        type: ActionType.setMyApps,
        payload: {
          appIds,
          appData,
        },
      });
    },
  }
);
export const reportJob = createApiCall("reportJob", api.reportJob, 400, {});
